import { useState, useEffect } from 'react';

export default function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalledButInBrowser, setIsInstalledButInBrowser] = useState(false);

  useEffect(() => {
    // ------------------------------------------------------------------
    // Robust platform detection
    // ------------------------------------------------------------------
    // 1) Classic iOS (iPhone / iPod) user-agents contain the strings below.
    // 2) Since iPadOS 13, Safari reports itself as a desktop browser and the
    //    user-agent no longer contains "iPad".  The recommended workaround
    //    is to detect "MacIntel" *and* a touch-capable screen.
    //    See: https://developer.apple.com/forums/thread/119186
    // ------------------------------------------------------------------
    const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;

    const isClassicIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isIPadOS     = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    const isIOSDevice = isClassicIOS || isIPadOS;

    // Android detection should ignore iOS/iPadOS matches
    const isAndroidDevice = /android/i.test(userAgent) && !isIOSDevice;

    const isDesktopDevice = !isIOSDevice && !isAndroidDevice;
    
    // ------------------------------------------------------------
    // Optional testing flag – allows developers on desktop/Android
    // to force the "iOS manual install" branch without spoofing the
    // user-agent.  Append `?testIOSPWA=true` to the URL.
    // ------------------------------------------------------------
    const urlParams = new URLSearchParams(window.location.search);
    const forceIOS = urlParams.get('testIOSPWA') === 'true' || urlParams.get('testIOSPWA') === '1';

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsDesktop(isDesktopDevice);

    // Override via testing flag
    if (forceIOS) {
      console.log('[PWA] testIOSPWA=TRUE – forcing iOS install flow');
      setIsIOS(true);
      setIsAndroid(false);
      setIsDesktop(false);
    }

    // Check if already installed (running in standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    // NEW: Check localStorage flag from previous install
    const wasInstalledFlag = localStorage.getItem('pwaInstalled') === 'true';
    if (wasInstalledFlag && isAndroidDevice && !isStandalone) {
      console.log('[PWA] Detected previous installation via localStorage flag');
      setIsInstalledButInBrowser(true);
    }

    // Check if app is installed but running in browser (Android specific)
    const checkInstalledButInBrowser = async () => {
      if (isAndroidDevice && !isStandalone) {
        // First try the getInstalledRelatedApps API if available
        let installed = false;
        if ('getInstalledRelatedApps' in navigator) {
          try {
            const apps = await navigator.getInstalledRelatedApps();
            installed = apps.some(app => app.platform === 'webapp');
          } catch (err) {
            console.warn('getInstalledRelatedApps failed:', err);
          }
        }
        setIsInstalledButInBrowser(installed);
        if (installed) {
          localStorage.setItem('pwaInstalled', 'true');
        }
      }
    };

    checkInstalledButInBrowser();

    // Listen for beforeinstallprompt event (Android and Desktop)
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
      // If we get the install prompt, the app is definitely not installed
      setIsInstalledButInBrowser(false);
      // Installation prompt appeared – ensure any stored flag is cleared
      localStorage.removeItem('pwaInstalled');
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      console.log('[PWA] App was installed');
      setIsInstalled(true);
      setDeferredPrompt(null);
      setIsInstallable(false);
      setIsInstalledButInBrowser(false);
      // Remember installation across reloads
      localStorage.setItem('pwaInstalled', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installPWA = async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No deferred prompt available');
      return false;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log(`[PWA] User response to install prompt: ${outcome}`);
      
      if (outcome === 'accepted') {
        console.log('[PWA] User accepted the install prompt');
        // ------------------------------------------------------
        // Mark as installed *immediately* so the UI can react
        // without waiting for the appinstalled event, which
        // may be delayed or not dispatched in some browsers.
        // ------------------------------------------------------
        setIsInstalled(true);
        // Clear states related to installability
        setDeferredPrompt(null);
        setIsInstallable(false);
        setIsInstalledButInBrowser(false);
        // Persist across reloads
        localStorage.setItem('pwaInstalled', 'true');
        return true;
      } else {
        console.log('[PWA] User dismissed the install prompt');
        return false;
      }
    } catch (error) {
      console.error('[PWA] Error during installation:', error);
      return false;
    }
  };

  const openInstalledApp = () => {
    if (isAndroid) {
      try {
        // Method 1: Create a hidden link and click it
        // This is more likely to trigger the "Open with" dialog
        const link = document.createElement('a');
        link.href = window.location.href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('[PWA] Triggered link click to open app');
        return true;
        
      } catch (error) {
        console.log('[PWA] Link click method failed:', error);
        
        try {
          // Method 2: Try using location.assign with a slight delay
          setTimeout(() => {
            const currentUrl = window.location.href;
            // Add a timestamp to make it slightly different
            const urlWithTimestamp = new URL(currentUrl);
            urlWithTimestamp.searchParams.set('t', Date.now().toString());
            
            // Use location.assign instead of href
            window.location.assign(urlWithTimestamp.toString());
          }, 100);
          
          console.log('[PWA] Attempting delayed navigation');
          return true;
          
        } catch (e) {
          console.log('[PWA] All methods failed:', e);
          
          // Show user guidance
          const userAgent = navigator.userAgent.toLowerCase();
          let message = 'To open in the app:\n';
          
          if (userAgent.includes('chrome')) {
            message += '• Tap the menu (⋮) and select "Open in app"\n• Or look for the app icon in your recent apps';
          } else {
            message += '• Use your browser menu to open in the app\n• Or switch to the app from your recent apps';
          }
          
          alert(message);
          return false;
        }
      }
    }
    
    return false;
  };

  return {
    isInstallable,
    isIOS,
    isAndroid,
    isDesktop,
    isInstalled,
    isInstalledButInBrowser,
    installPWA,
    openInstalledApp,
    canInstall: isInstallable || (isIOS && !isInstalled),
    shouldShowOpenInApp: isAndroid && isInstalledButInBrowser && !isInstalled
  };
} 